import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { EditorHeader } from "./EditorHeader";

export const FundsEditor = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: fundAllocation, isLoading } = useQuery({
    queryKey: ['fund-allocation'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fund_allocation')
        .select('*, fund_breakdown_items(*)');
      
      if (error) throw error;
      return data;
    },
  });

  const handleSave = async (e: React.FormEvent<HTMLFormElement>, allocationId: string) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const updates = {
      category: String(formData.get('category')),
      amount: Number(formData.get('amount')),
      percentage: Number(formData.get('percentage')),
      description: String(formData.get('description')),
      color: String(formData.get('color')),
    };

    try {
      const { error } = await supabase
        .from('fund_allocation')
        .update(updates)
        .eq('id', allocationId);

      if (error) throw error;

      // Update breakdown items
      const breakdownItems = formData.getAll('breakdown_item').map((item, index) => ({
        item: String(item),
        allocation: Number(formData.getAll('breakdown_allocation')[index]),
        fund_allocation_id: allocationId,
      }));

      // Delete existing breakdown items
      await supabase
        .from('fund_breakdown_items')
        .delete()
        .eq('fund_allocation_id', allocationId);

      // Insert new breakdown items
      const { error: breakdownError } = await supabase
        .from('fund_breakdown_items')
        .insert(breakdownItems);

      if (breakdownError) throw breakdownError;

      toast({
        title: "Success",
        description: "Fund allocation has been updated",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update fund allocation",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdd = async () => {
    try {
      const { data: allocation, error } = await supabase
        .from('fund_allocation')
        .insert({
          category: 'New Category',
          amount: 0,
          percentage: 0,
          description: 'Description',
          color: '#000000',
        })
        .select()
        .single();

      if (error) throw error;

      const { error: breakdownError } = await supabase
        .from('fund_breakdown_items')
        .insert({
          item: 'New Item',
          allocation: 0,
          fund_allocation_id: allocation.id,
        });

      if (breakdownError) throw breakdownError;

      toast({
        title: "Success",
        description: "New fund allocation has been added",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add fund allocation",
      });
    }
  };

  const handleDelete = async (allocationId: string) => {
    try {
      // Delete breakdown items first due to foreign key constraint
      await supabase
        .from('fund_breakdown_items')
        .delete()
        .eq('fund_allocation_id', allocationId);

      const { error } = await supabase
        .from('fund_allocation')
        .delete()
        .eq('id', allocationId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Fund allocation has been deleted",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete fund allocation",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditorHeader title="Fund Allocation Editor" />
      <Button onClick={handleAdd} className="mb-4">
        <Plus className="h-4 w-4 mr-2" />
        Add Fund Allocation
      </Button>

      <div className="space-y-6">
        {fundAllocation?.map((allocation) => (
          <Card key={allocation.id}>
            <CardContent className="pt-6">
              <form onSubmit={(e) => handleSave(e, allocation.id)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Category</label>
                    <Input
                      name="category"
                      defaultValue={allocation.category}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Color</label>
                    <Input
                      name="color"
                      type="color"
                      defaultValue={allocation.color}
                      className="mt-1 h-10"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Amount</label>
                    <Input
                      name="amount"
                      type="number"
                      defaultValue={allocation.amount}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Percentage</label>
                    <Input
                      name="percentage"
                      type="number"
                      defaultValue={allocation.percentage}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Textarea
                    name="description"
                    defaultValue={allocation.description}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                
                <div className="space-y-4">
                  <label className="text-sm font-medium">Breakdown Items</label>
                  {allocation.fund_breakdown_items.map((item: any, index: number) => (
                    <div key={item.id} className="grid grid-cols-2 gap-4">
                      <div>
                        <Input
                          name="breakdown_item"
                          defaultValue={item.item}
                          placeholder="Item"
                        />
                      </div>
                      <div>
                        <Input
                          name="breakdown_allocation"
                          type="number"
                          defaultValue={item.allocation}
                          placeholder="Allocation"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between">
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                  <Button 
                    type="button"
                    variant="destructive"
                    onClick={() => handleDelete(allocation.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};